// Map styling constants
export const COLORS = {
  SUBJECT: '#FFD700',      // Gold
  SELECTED: '#4169E1',     // Blue
  REDUCTION: '#15803d',    // Green
  NO_REDUCTION: '#ef4444', // Red
  MIXED: '#f97316',        // Orange
  WHITE: '#FFFFFF',
  BORDER: '#FFFFFF'
};

export const RADII = {
  SUBJECT: 10,
  SELECTED: 10,
  DEFAULT: 7,
  CLUSTER: {
    SMALL: 10,
    MEDIUM: 12,
    LARGE: 14
  }
};

export const STROKE_WIDTHS = {
  SUBJECT: 2,
  SELECTED: 2,
  MODIFIED: 3,
  DEFAULT: 1
};

// Function to get the base paint properties for a layer
export const getBasePaintProperties = (layerId) => ({
  'circle-radius': [
    'case',
    ['==', ['get', 'isSubject'], true], RADII.SUBJECT,
    ['==', ['get', 'selected'], true], RADII.SELECTED,
    ['==', ['get', 'isModified'], true], RADII.SUBJECT,
    RADII.DEFAULT
  ],
  'circle-color': [
    'case',
    ['==', ['get', 'isSubject'], true], COLORS.SUBJECT,
    ['==', ['get', 'selected'], true], COLORS.SELECTED,
    ['==', ['get', 'supportsReduction'], true], COLORS.REDUCTION,
    COLORS.NO_REDUCTION
  ],
  'circle-stroke-width': [
    'case',
    ['==', ['get', 'isModified'], true], STROKE_WIDTHS.MODIFIED,
    ['==', ['get', 'isSubject'], true], STROKE_WIDTHS.SUBJECT,
    ['==', ['get', 'selected'], true], STROKE_WIDTHS.SELECTED,
    STROKE_WIDTHS.DEFAULT
  ],
  'circle-stroke-color': [
    'case',
    ['==', ['get', 'isModified'], true], COLORS.SUBJECT,
    COLORS.BORDER
  ]
});

// Function to get selected point properties
export const getSelectedPointProperties = (pointId) => ({
  'circle-color': [
    'case',
    ['==', ['get', 'parcel_id'], pointId], COLORS.WHITE,
    ['==', ['get', 'isSubject'], true], COLORS.SUBJECT,
    ['==', ['get', 'supportsReduction'], true], COLORS.REDUCTION,
    COLORS.NO_REDUCTION
  ],
  'circle-stroke-color': [
    'case',
    ['==', ['get', 'parcel_id'], pointId], COLORS.SELECTED,
    COLORS.BORDER
  ],
  'circle-stroke-width': [
    'case',
    ['==', ['get', 'parcel_id'], pointId], STROKE_WIDTHS.MODIFIED,
    ['==', ['get', 'isSubject'], true], STROKE_WIDTHS.SUBJECT,
    STROKE_WIDTHS.DEFAULT
  ]
});

// Function to get cluster paint properties
export const getClusterPaintProperties = () => ({
  'circle-color': [
    'case',
    ['==', ['get', 'has_subject'], true], COLORS.SUBJECT,  // Yellow if contains subject
    ['==', ['get', 'has_selected'], true], COLORS.SELECTED, // Blue if contains selected comp
    ['==', ['get', 'all_green'], true], COLORS.REDUCTION,   // Green if all support reduction
    ['==', ['get', 'all_red'], true], COLORS.NO_REDUCTION,  // Red if all no reduction
    COLORS.MIXED  // Orange for mixed clusters
  ],
  'circle-radius': [
    'step',
    ['get', 'point_count'],
    RADII.CLUSTER.SMALL,
    10, RADII.CLUSTER.MEDIUM,
    25, RADII.CLUSTER.LARGE
  ],
  'circle-stroke-width': STROKE_WIDTHS.SUBJECT,
  'circle-stroke-color': COLORS.BORDER
});

// Function to update point styling for a specific point
export const updatePointStyling = (map, layerIds, pointId) => {
  layerIds.forEach(layerId => {
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, 'circle-color', [
        'case',
        ['==', ['get', 'parcel_id'], pointId],
        COLORS.WHITE,
        layerId === 'subject-layer' ? COLORS.SUBJECT :
        layerId === 'selected-comps-layer' ? COLORS.SELECTED :
        layerId === 'reduction-comps-layer' ? COLORS.REDUCTION : COLORS.NO_REDUCTION
      ]);

      map.setPaintProperty(layerId, 'circle-stroke-color', [
        'case',
        ['==', ['get', 'parcel_id'], pointId],
        COLORS.SELECTED,
        COLORS.BORDER
      ]);

      map.setPaintProperty(layerId, 'circle-stroke-width', [
        'case',
        ['==', ['get', 'parcel_id'], pointId],
        STROKE_WIDTHS.MODIFIED,
        layerId === 'subject-layer' ? STROKE_WIDTHS.SUBJECT : STROKE_WIDTHS.DEFAULT
      ]);

      map.setPaintProperty(layerId, 'circle-radius', [
        'case',
        ['==', ['get', 'parcel_id'], pointId],
        layerId === 'subject-layer' ? RADII.SUBJECT :
        layerId === 'selected-comps-layer' ? RADII.SELECTED : RADII.DEFAULT,
        layerId === 'subject-layer' ? RADII.SUBJECT :
        layerId === 'selected-comps-layer' ? RADII.SELECTED : RADII.DEFAULT
      ]);
    }
  });
};

// Function to reset point styling for a specific layer
export const resetLayerStyling = (map, layerId, preserveSelectedComps = false) => {
  if (!map || !map.getLayer(layerId)) return;

  // For selected-comps-layer, we might want to preserve its styling
  if (layerId === 'selected-comps-layer' && preserveSelectedComps) {
    return;
  }

  map.setPaintProperty(layerId, 'circle-color', 
    layerId === 'subject-layer' ? COLORS.SUBJECT :
    layerId === 'selected-comps-layer' ? COLORS.SELECTED :
    layerId === 'reduction-comps-layer' ? COLORS.REDUCTION : COLORS.NO_REDUCTION
  );
  map.setPaintProperty(layerId, 'circle-stroke-color', COLORS.BORDER);
  map.setPaintProperty(layerId, 'circle-stroke-width', 
    layerId === 'subject-layer' ? STROKE_WIDTHS.SUBJECT :
    layerId === 'selected-comps-layer' ? STROKE_WIDTHS.SELECTED : STROKE_WIDTHS.DEFAULT
  );
};

// Function to reset all point layers styling
export const resetAllPointLayers = (map, preserveSelectedComps = false) => {
  if (!map) return;

  const layers = ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer'];
  layers.forEach(layerId => resetLayerStyling(map, layerId, preserveSelectedComps));
};

// Function to reset cluster styling
export const resetClusterStyling = (map) => {
  if (!map || !map.getLayer('clusters')) return;

  map.setPaintProperty('clusters', 'circle-stroke-color', COLORS.BORDER);
  map.setPaintProperty('clusters', 'circle-stroke-width', STROKE_WIDTHS.SUBJECT);
};

// Function to reset all styling (points and clusters)
export const resetAllStyling = (map, preserveSelectedComps = false) => {
  if (!map) return;

  // Reset all point layers
  ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer'].forEach(layerId => {
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, 'circle-color', 
        layerId === 'subject-layer' ? COLORS.SUBJECT :
        layerId === 'selected-comps-layer' ? COLORS.SELECTED :
        layerId === 'reduction-comps-layer' ? COLORS.REDUCTION : COLORS.NO_REDUCTION
      );
      map.setPaintProperty(layerId, 'circle-stroke-color', COLORS.BORDER);
      map.setPaintProperty(layerId, 'circle-stroke-width', 
        layerId === 'subject-layer' ? STROKE_WIDTHS.SUBJECT : STROKE_WIDTHS.DEFAULT
      );
    }
  });

  // Reset cluster styling
  if (map.getLayer('clusters')) {
    const clusterProperties = getClusterPaintProperties();
    Object.entries(clusterProperties).forEach(([property, value]) => {
      map.setPaintProperty('clusters', property, value);
    });
  }
};

// Function to update cluster styling
export const updateClusterStyling = (map, clusterId = null) => {
  if (!map || !map.getLayer('clusters')) return;

  if (clusterId) {
    // When a cluster is selected, update the filter to only highlight the selected cluster
    map.setFilter('clusters', [
      'all',
      ['has', 'point_count'],
      ['==', ['get', 'cluster_id'], clusterId]
    ]);
    map.setPaintProperty('clusters', 'circle-stroke-color', COLORS.SELECTED);
    map.setPaintProperty('clusters', 'circle-stroke-width', STROKE_WIDTHS.MODIFIED);
  } else {
    // When no cluster is selected, show all clusters with default styling
    map.setFilter('clusters', ['has', 'point_count']);
    map.setPaintProperty('clusters', 'circle-stroke-color', COLORS.BORDER);
    map.setPaintProperty('clusters', 'circle-stroke-width', STROKE_WIDTHS.SUBJECT);
  }
};

// Function to get subject sale indicator layer properties
export const getSubjectSaleIndicatorLayer = () => ({
  id: 'subject-sale-indicator',
  type: 'symbol',
  source: 'comps',
  filter: ['all',
    ['==', ['get', 'isSubject'], true],
    ['==', ['get', 'isSubjectSale'], true]
  ],
  layout: {
    'text-field': '$',
    'text-size': 14,
    'text-font': ['Open Sans Bold'],
    'text-justify': 'center',
    'text-anchor': 'center',
    'text-allow-overlap': true,
    'text-offset': [0, 0]
  },
  paint: {
    'text-color': [
      'case',
      ['>', ['get', 'salePrice'], ['get', 'ifmv']],
      COLORS.NO_REDUCTION,  // Red for high sales
      COLORS.REDUCTION     // Green for low sales
    ],
    'text-halo-color': '#000000',
    'text-halo-width': 0
  }
});

// Function to get selected comps text layer properties
// Shows row number, or "X" if ALFlag === 0
export const getSelectedCompsTextLayer = () => ({
  id: 'selected-comps-text',
  type: 'symbol',
  source: 'comps',
  filter: ['all', 
    ['==', 'selected', true],
    ['==', 'isSubject', false],
    ['>', 'selectedRowNumber', 0]
  ],
  layout: {
    'text-field': [
      'case',
      ['any',
        ['==', ['get', 'ALFlag'], 0],
        ['==', ['get', 'ALFlag'], '0']
      ], 'X',
      ['to-string', ['get', 'selectedRowNumber']]
    ],
    'text-size': [
      'case',
      ['any',
        ['==', ['get', 'ALFlag'], 0],
        ['==', ['get', 'ALFlag'], '0']
      ], 14, // Reduced by 20% from 18
      12
    ],
    'text-font': ['Open Sans Bold'],
    'text-justify': 'center',
    'text-anchor': 'center',
    'text-allow-overlap': true
  },
  paint: {
    'text-color': [
      'case',
      ['any',
        ['==', ['get', 'ALFlag'], 0],
        ['==', ['get', 'ALFlag'], '0']
      ], COLORS.NO_REDUCTION,
      COLORS.WHITE
    ],
    'text-halo-color': [
      'case',
      ['any',
        ['==', ['get', 'ALFlag'], 0],
        ['==', ['get', 'ALFlag'], '0']
      ], COLORS.WHITE,
      '#000000'
    ],
    'text-halo-width': [
      'case',
      ['any',
        ['==', ['get', 'ALFlag'], 0],
        ['==', ['get', 'ALFlag'], '0']
      ], 2,
      1
    ]
  }
});

// Function to get exclamation mark layer properties
export const getExclamationLayer = () => ({
  id: 'exclamation-layer',
  type: 'symbol',
  source: 'comps',
  layout: {
    'text-field': '!',
    'text-size': 12,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-offset': [0, 0],
    'text-anchor': 'center'
  },
  paint: {
    'text-color': COLORS.NO_REDUCTION, // Red exclamation mark
    'text-halo-color': COLORS.WHITE,
    'text-halo-width': 1
  },
  filter: ['==', ['get', 'isModified'], true]
});

// Function to get cluster count layer properties
export const getClusterCountLayer = () => ({
  id: 'cluster-count',
  type: 'symbol',
  source: 'comps',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': [
      'step',
      ['get', 'point_count'],
      ['concat', '+', ['to-string', ['get', 'point_count']]],
      50, '50+'
    ],
    'text-font': ['Open Sans Bold'],
    'text-size': 12,
    'text-allow-overlap': true
  },
  paint: {
    'text-color': COLORS.WHITE,
    'text-halo-color': '#000000',
    'text-halo-width': 1
  }
});

// Function to get moved points layer properties
export const getMovedPointsLayer = () => ({
  id: 'moved-points-layer',
  type: 'circle',
  source: 'comps',
  filter: ['==', ['get', 'isModified'], true],
  paint: {
    'circle-radius': RADII.SUBJECT,
    'circle-color': COLORS.WHITE,
    'circle-stroke-width': STROKE_WIDTHS.MODIFIED,
    'circle-stroke-color': COLORS.SUBJECT
  }
});

// Function to get moved points text layer properties
export const getMovedPointsTextLayer = () => ({
  id: 'moved-points-text',
  type: 'symbol',
  source: 'comps',
  filter: ['==', ['get', 'isModified'], true],
  layout: {
    'text-field': '!',
    'text-size': 12,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-offset': [0, 0],
    'text-anchor': 'center'
  },
  paint: {
    'text-color': COLORS.NO_REDUCTION,
    'text-halo-color': COLORS.WHITE,
    'text-halo-width': 1
  }
});

// Function to get unselected comps NAL indicator layer properties
// Shows a red "X" for unselected comps where ALFlag === 0 (explicitly, not empty/null)
export const getUnselectedCompsNALTextLayer = () => ({
  id: 'unselected-comps-nal-text',
  type: 'symbol',
  source: 'comps',
  filter: ['all',
    ['==', ['get', 'isSubject'], false],
    ['==', ['get', 'selected'], false],
    ['any',
      ['==', ['get', 'ALFlag'], 0],
      ['==', ['get', 'ALFlag'], '0']
    ]
  ],
  layout: {
    'text-field': 'X',
    'text-size': 14, // Reduced by 20% from 18
    'text-font': ['Open Sans Bold'],
    'text-justify': 'center',
    'text-anchor': 'center',
    'text-allow-overlap': true,
    'text-ignore-placement': true
  },
  paint: {
    'text-color': COLORS.NO_REDUCTION, // Red color
    'text-halo-color': COLORS.WHITE,
    'text-halo-width': 2
  }
}); 